import React, { useMemo } from "react";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChanges";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Calendar } from "primereact/calendar";
import {
  CALENDAR_DISPLAY_DATE_FORMAT,
  CALENDAR_LOCALE,
} from "../../utils/constants/calendar-constants";
import { Button } from "primereact/button";
import { FormSection, FormSections } from "../ui";

import {
  useCreateWorkshopMutation,
  useUpdateWorkshopMutation,
  useGetWorkshopByIdQuery,
} from "../../store/services/workshops";
import { useGetTenantsQuery } from "../../store/services/tenants";
import { useTenantScope } from "../../hooks/useTenantScope";
import { WorkshopWithMetrics, WorkshopStatus } from "../../models/workshops";
import { workshopStatusOptions } from "../../utils/constants/workshops-constants";
import { InputFileCustom } from "../InputFileCustom";
import { ImageUrlsManager } from "../ImageUrlsManager";

interface ScheduleEntry {
  day: string;
  startTime: string;
  endTime: string;
}

const dayOptions = [
  { label: "Lunes", value: "Lunes" },
  { label: "Martes", value: "Martes" },
  { label: "Miércoles", value: "Miércoles" },
  { label: "Jueves", value: "Jueves" },
  { label: "Viernes", value: "Viernes" },
  { label: "Sábado", value: "Sábado" },
  { label: "Domingo", value: "Domingo" },
];

interface WorkshopFormProps {
  workshopData?: WorkshopWithMetrics;
  refetchWorkshopData?: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required("El nombre es obligatorio").max(256),
  activityType: Yup.string().required("El tipo de actividad es obligatorio"),
  tenantId: Yup.string().required("El tenant es obligatorio"),
  durationMonths: Yup.number()
    .required("La duración es obligatoria")
    .min(1, "Mínimo 1 mes")
    .max(8, "Máximo 8 meses"),
  maxCapacity: Yup.number()
    .required("La capacidad es obligatoria")
    .min(1, "Mínimo 1"),
  monthlyPrice: Yup.number()
    .nullable()
    .when("isPaid", {
      is: true,
      then: (schema) =>
        schema.required("El precio es obligatorio para talleres pagos").min(0),
    }),
  startDate: Yup.date()
    .nullable()
    .required("La fecha de inicio es obligatoria"),
  endDate: Yup.date()
    .nullable()
    .required("La fecha de fin es obligatoria")
    .test(
      "end-after-start",
      "La fecha de fin debe ser posterior a la fecha de inicio",
      function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return value > startDate;
      }
    ),
  registrationDeadline: Yup.date()
    .nullable()
    .test(
      "deadline-before-start",
      "La deadline de inscripción debe ser anterior o igual a la fecha de inicio del taller. No puede estar dentro del rango del curso.",
      function (value) {
        const { startDate, endDate } = this.parent;
        if (!value) return true;
        if (startDate && value > startDate) return false;
        if (endDate && value >= endDate) return false;
        return true;
      }
    ),
  buttonType: Yup.string()
    .oneOf(["INSCRIPTION", "CUSTOM_LINK", "NONE"])
    .required(),
  buttonLabel: Yup.string()
    .nullable()
    .max(40, "Máximo 40 caracteres")
    .when("buttonType", {
      is: "CUSTOM_LINK",
      then: (schema) =>
        schema.required("La etiqueta del botón es obligatoria").trim(),
    }),
  buttonUrl: Yup.string()
    .nullable()
    .when("buttonType", {
      is: "CUSTOM_LINK",
      then: (schema) =>
        schema
          .required("La URL es obligatoria")
          .matches(/^https:\/\//, "La URL debe iniciar con https://"),
    }),
});

export const WorkshopForm = ({
  workshopData,
  refetchWorkshopData,
}: WorkshopFormProps) => {
  const navigate = useNavigate();
  const { data: tenants } = useGetTenantsQuery({});
  const tenantScope = useTenantScope();
  const [createWorkshop, { isLoading: isCreating }] = useCreateWorkshopMutation();
  const [updateWorkshop, { isLoading: isUpdating }] = useUpdateWorkshopMutation();

  const entityId = workshopData?.id != null ? String(workshopData.id) : "";
  const { data: cachedWorkshop, isFetching: isEntityFetching } =
    useGetWorkshopByIdQuery(entityId, { skip: !entityId });
  const entity = cachedWorkshop ?? workshopData;

  const isLoading = isCreating || isUpdating;
  const isSubmitBlocked = isEntityFetching || isLoading;

  const tenantOptions = React.useMemo(() => {
    if (!Array.isArray(tenants)) return [];
    return tenants.map((t: any) => ({ label: t.provider, value: String(t.id) }));
  }, [tenants]);

  const formik = useFormik({
    initialValues: {
      name: entity?.name || "",
      description: entity?.description || "",
      activityType: entity?.activityType || "",
      tenantId: entity?.tenantId || "",
      durationMonths: entity?.durationMonths || 1,
      startDate: entity?.startDate
        ? new Date(entity.startDate * 1000)
        : null,
      endDate: entity?.endDate
        ? new Date(entity.endDate * 1000)
        : null,
      isRolling: entity?.isRolling || false,
      isPaid: entity?.isPaid || false,
      monthlyPrice: entity?.monthlyPrice ?? null,
      schedule: entity?.schedule || "[]",
      maxCapacity: entity?.maxCapacity || 20,
      location: entity?.location || "",
      instructorName: entity?.instructorName || "",
      status: (entity?.status || "draft") as WorkshopStatus,
      imageUrl: entity?.imageUrl || "",
      imageUrls: entity?.imageUrls || [],
      registrationDeadline: entity?.registrationDeadline
        ? new Date(entity.registrationDeadline * 1000)
        : null,
      buttonType: (entity?.buttonType || "INSCRIPTION") as
        | "INSCRIPTION"
        | "CUSTOM_LINK"
        | "NONE",
      buttonLabel: entity?.buttonLabel || "",
      buttonUrl: entity?.buttonUrl || "",
      showAttendeesInfo:
        (entity?.buttonType || "INSCRIPTION") !== "INSCRIPTION"
          ? false
          : entity?.showAttendeesInfo ?? true,
    },
    enableReinitialize: !!entity,
    validationSchema,
    onSubmit: (values) => {
      if (isEntityFetching) return;

      const payload = {
        tenantId: values.tenantId,
        name: values.name,
        description: values.description || null,
        activityType: values.activityType,
        durationMonths: values.durationMonths,
        startDate: values.startDate
          ? Math.floor(values.startDate.getTime() / 1000)
          : null,
        endDate: values.endDate
          ? Math.floor(values.endDate.getTime() / 1000)
          : null,
        isRolling: values.isRolling,
        isPaid: values.isPaid,
        monthlyPrice: values.isPaid && values.monthlyPrice
          ? values.monthlyPrice
          : null,
        schedule: values.schedule,
        maxCapacity: values.maxCapacity,
        location: values.location || null,
        instructorName: values.instructorName || null,
        status: values.status,
        imageUrl: values.imageUrl || null,
        imageUrls: values.imageUrls || [],
        registrationDeadline: values.registrationDeadline
          ? Math.floor(values.registrationDeadline.getTime() / 1000)
          : null,
        buttonType: values.buttonType,
        buttonLabel:
          values.buttonType === "CUSTOM_LINK"
            ? values.buttonLabel?.trim() || null
            : null,
        buttonUrl:
          values.buttonType === "CUSTOM_LINK"
            ? values.buttonUrl?.trim() || null
            : null,
        // Los cupos/asistentes solo se exponen en la app cuando la inscripción
        // se gestiona dentro de ella.
        showAttendeesInfo:
          values.buttonType === "INSCRIPTION" ? values.showAttendeesInfo : false,
      };

      if (workshopData) {
        updateWorkshop({ id: workshopData.id, ...payload })
          .unwrap()
          .then(() => {
            toast.success("Taller actualizado exitosamente!");
            refetchWorkshopData?.();
            navigate(-1);
          })
          .catch((err) => {
            toast.error(
              err?.data?.error?.message || "Error al actualizar el taller"
            );
          });
      } else {
        createWorkshop(payload)
          .unwrap()
          .then(() => {
            toast.success("Taller creado exitosamente!");
            navigate("/workshops");
          })
          .catch((err) => {
            toast.error(
              err?.data?.error?.message || "Error al crear el taller"
            );
          });
      }
    },
  });

  // Avisa antes de que un cambio de tenant en el header recargue la pantalla
  // y se lleve puesto lo que se estaba cargando.
  useUnsavedChangesGuard(formik.dirty);

  const {
    handleSubmit,
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
  } = formik;

  // MT-011 — Si el tenant está fijado por el scope, forzar el valor del form.
  React.useEffect(() => {
    if (tenantScope.locked && tenantScope.tenantId && values.tenantId !== tenantScope.tenantId) {
      setFieldValue("tenantId", tenantScope.tenantId);
    }
  }, [tenantScope.locked, tenantScope.tenantId, values.tenantId, setFieldValue]);

  const scheduleEntries: ScheduleEntry[] = useMemo(() => {
    try {
      const parsed = JSON.parse(values.schedule);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [values.schedule]);

  const addScheduleEntry = () => {
    const updated = [...scheduleEntries, { day: "Lunes", startTime: "09:00", endTime: "10:00" }];
    setFieldValue("schedule", JSON.stringify(updated));
  };

  const removeScheduleEntry = (index: number) => {
    const updated = scheduleEntries.filter((_, i) => i !== index);
    setFieldValue("schedule", JSON.stringify(updated));
  };

  const updateScheduleEntry = (index: number, field: keyof ScheduleEntry, value: string) => {
    const updated = scheduleEntries.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    );
    setFieldValue("schedule", JSON.stringify(updated));
  };

  return (
    <form onSubmit={handleSubmit} className="p-fluid">
      <FormSections>
      <FormSection title="Información General">
        <div className="grid">
          <div className="col-12 lg:col-6 field">
            <label htmlFor="name">Nombre *</label>
            <InputText
              id="name"
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={touched.name && errors.name ? "p-invalid" : ""}
            />
            {touched.name && errors.name && (
              <small className="p-error">{errors.name}</small>
            )}
          </div>

          <div className="col-12 lg:col-6 field">
            <label htmlFor="activityType">Tipo de Actividad *</label>
            <InputText
              id="activityType"
              name="activityType"
              value={values.activityType}
              onChange={handleChange}
              onBlur={handleBlur}
              className={
                touched.activityType && errors.activityType ? "p-invalid" : ""
              }
            />
            {touched.activityType && errors.activityType && (
              <small className="p-error">{errors.activityType}</small>
            )}
          </div>

          <div className="col-12 field">
            <label htmlFor="description">Descripción</label>
            <InputTextarea
              id="description"
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              rows={3}
            />
          </div>

          {!tenantScope.hidden && (
            <div className="col-12 lg:col-6 field">
              <label htmlFor="tenantId">Tenant *</label>
              <Dropdown
                id="tenantId"
                value={values.tenantId}
                onChange={(e) => setFieldValue("tenantId", e.value)}
                options={tenantOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Seleccionar tenant"
                disabled={tenantScope.locked}
                className={
                  touched.tenantId && errors.tenantId ? "p-invalid" : ""
                }
              />
              {tenantScope.locked && (
                <small className="text-color-secondary">
                  Fijado por el tenant seleccionado en la barra superior.
                </small>
              )}
              {touched.tenantId && errors.tenantId && (
                <small className="p-error">{errors.tenantId}</small>
              )}
            </div>
          )}

          <div className="col-12 lg:col-6 field">
            <label htmlFor="status">Estado</label>
            <Dropdown
              id="status"
              value={values.status}
              onChange={(e) => setFieldValue("status", e.value)}
              options={workshopStatusOptions}
              optionLabel="label"
              optionValue="value"
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Configuración">
        <div className="grid">
          <div className="col-12 lg:col-4 field">
            <label htmlFor="durationMonths">Duración de la mensualidad (meses) *</label>
            <InputNumber
              id="durationMonths"
              value={values.durationMonths}
              onValueChange={(e) => setFieldValue("durationMonths", e.value)}
              min={1}
              max={8}
              showButtons
              className={
                touched.durationMonths && errors.durationMonths ? "p-invalid" : ""
              }
            />
            <small className="text-color-secondary block mt-1">
              Cantidad de meses que cubre cada cobro (pago mensual).
            </small>
            {touched.durationMonths && errors.durationMonths && (
              <small className="p-error">{errors.durationMonths}</small>
            )}
          </div>

          <div className="col-12 lg:col-4 field">
            <label htmlFor="maxCapacity">Capacidad Máxima *</label>
            <InputNumber
              id="maxCapacity"
              value={values.maxCapacity}
              onValueChange={(e) => setFieldValue("maxCapacity", e.value)}
              min={1}
              showButtons
              className={
                touched.maxCapacity && errors.maxCapacity ? "p-invalid" : ""
              }
            />
            {touched.maxCapacity && errors.maxCapacity && (
              <small className="p-error">{errors.maxCapacity}</small>
            )}
          </div>

          <div className="col-12 lg:col-4 field">
            <label htmlFor="instructorName">Instructor</label>
            <InputText
              id="instructorName"
              name="instructorName"
              value={values.instructorName}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="col-12 lg:col-6 field">
            <label htmlFor="location">Ubicación</label>
            <InputText
              id="location"
              name="location"
              value={values.location}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="col-12 field">
            <label className="font-semibold block mb-2">Imagen Principal (1290x400)</label>
            <small className="text-500 block mb-2">
              Se muestra como portada del taller. Resolución mínima: 1290x400px.
            </small>
            <div
              className="border-1 surface-border border-round p-3"
              style={{ background: "#f8f9fa" }}
            >
              <InputFileCustom
                handleInputFile={(url) => setFieldValue("imageUrl", url)}
                handleInputFileClear={() => setFieldValue("imageUrl", "")}
                disabled={isLoading}
                initialImage={values.imageUrl}
                cropWidth={1290}
                cropHeight={400}
                uploadFolder="covers"
              />
            </div>
          </div>

          <div className="col-12 field">
            <ImageUrlsManager
              imageUrls={values.imageUrls || []}
              onChange={(urls) => setFieldValue("imageUrls", urls)}
              label="Imágenes Adicionales (1290x900)"
              cropWidth={1290}
              cropHeight={900}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 lg:col-3 field flex align-items-center gap-3 mt-3">
            <InputSwitch
              checked={values.isRolling}
              onChange={(e) => {
                setFieldValue("isRolling", e.value);
                if (e.value) {
                  setFieldValue("registrationDeadline", null);
                }
              }}
            />
            <label>Inscripción continua (Rolling)</label>
          </div>

          <div className="col-12 lg:col-3 field flex align-items-center gap-3 mt-3">
            <InputSwitch
              checked={values.isPaid}
              onChange={(e) => setFieldValue("isPaid", e.value)}
            />
            <label>Taller pago</label>
          </div>

          {values.isPaid && (
            <div className="col-12 lg:col-6 field">
              <label htmlFor="monthlyPrice">Precio Mensual ($) *</label>
              <InputNumber
                id="monthlyPrice"
                value={values.monthlyPrice}
                onValueChange={(e) => setFieldValue("monthlyPrice", e.value)}
                min={0}
                mode="currency"
                currency="ARS"
                locale="es-AR"
                className={
                  touched.monthlyPrice && errors.monthlyPrice ? "p-invalid" : ""
                }
              />
              {touched.monthlyPrice && errors.monthlyPrice && (
                <small className="p-error">{errors.monthlyPrice as string}</small>
              )}
            </div>
          )}
        </div>
      </FormSection>

      <FormSection title="Horarios">
        <div className="flex flex-column gap-3">
          {scheduleEntries.map((entry, index) => (
            <div key={index} className="grid align-items-end">
              <div className="col-12 lg:col-3 field mb-0">
                <label>Día</label>
                <Dropdown
                  value={entry.day}
                  onChange={(e) => updateScheduleEntry(index, "day", e.value)}
                  options={dayOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleccionar día"
                  className="w-full"
                />
              </div>
              <div className="col-12 lg:col-3 field mb-0">
                <label>Hora inicio</label>
                <InputText
                  value={entry.startTime}
                  onChange={(e) => updateScheduleEntry(index, "startTime", e.target.value)}
                  placeholder="09:00"
                  className="w-full"
                />
              </div>
              <div className="col-12 lg:col-3 field mb-0">
                <label>Hora fin</label>
                <InputText
                  value={entry.endTime}
                  onChange={(e) => updateScheduleEntry(index, "endTime", e.target.value)}
                  placeholder="10:00"
                  className="w-full"
                />
              </div>
              <div className="col-12 lg:col-3 field mb-0">
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  outlined
                  type="button"
                  onClick={() => removeScheduleEntry(index)}
                  tooltip="Eliminar"
                />
              </div>
            </div>
          ))}
          {scheduleEntries.length === 0 && (
            <p className="text-color-secondary m-0">No hay horarios cargados.</p>
          )}
          <Button
            label="Agregar horario"
            icon="pi pi-plus"
            outlined
            type="button"
            onClick={addScheduleEntry}
            className="align-self-start"
          />
        </div>
      </FormSection>

      <FormSection title="Fechas">
        <div className="grid">
          <div className="col-12 lg:col-4 field">
            <label htmlFor="startDate">Fecha de Inicio *</label>
            <Calendar
              id="startDate"
              value={values.startDate}
              onChange={(e) => setFieldValue("startDate", e.value)}
              dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
              locale={CALENDAR_LOCALE}
              showIcon
              className={
                touched.startDate && errors.startDate ? "p-invalid" : ""
              }
            />
            {touched.startDate && errors.startDate && (
              <small className="p-error">{errors.startDate as string}</small>
            )}
          </div>

          <div className="col-12 lg:col-4 field">
            <label htmlFor="endDate">Fecha de Fin *</label>
            <Calendar
              id="endDate"
              value={values.endDate}
              onChange={(e) => setFieldValue("endDate", e.value)}
              dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
              locale={CALENDAR_LOCALE}
              showIcon
              className={
                touched.endDate && errors.endDate ? "p-invalid" : ""
              }
            />
            {touched.endDate && errors.endDate && (
              <small className="p-error">{errors.endDate as string}</small>
            )}
          </div>

          {!values.isRolling && (
            <div className="col-12 lg:col-4 field">
              <label htmlFor="registrationDeadline">Deadline de Inscripción</label>
              <Calendar
                id="registrationDeadline"
                value={values.registrationDeadline}
                onChange={(e) => setFieldValue("registrationDeadline", e.value)}
                dateFormat={CALENDAR_DISPLAY_DATE_FORMAT}
              locale={CALENDAR_LOCALE}
                showTime
                hourFormat="24"
                showIcon
                className={
                  touched.registrationDeadline && errors.registrationDeadline
                    ? "p-invalid"
                    : ""
                }
              />
              {touched.registrationDeadline && errors.registrationDeadline && (
                <small className="p-error">
                  {errors.registrationDeadline as string}
                </small>
              )}
            </div>
          )}

          {values.isRolling && (
            <div className="col-12">
              <p className="text-color-secondary mt-2">
                Los talleres con inscripción continua (rolling) permiten que los usuarios se inscriban en cualquier momento. Las fechas son opcionales pero se pueden definir para acotar el período del taller.
              </p>
            </div>
          )}
        </div>
      </FormSection>

      <FormSection title="Acción / CTA">
        <div className="grid">
          <div className="col-12 lg:col-4 field">
            <label htmlFor="buttonType">Modalidad del botón *</label>
            <Dropdown
              id="buttonType"
              value={values.buttonType}
              options={[
                { label: "Inscripción estándar", value: "INSCRIPTION" },
                { label: "Botón personalizado", value: "CUSTOM_LINK" },
                { label: "Sin botón (informativo)", value: "NONE" },
              ]}
              onChange={(e) => {
                setFieldValue("buttonType", e.value);
                if (e.value !== "CUSTOM_LINK") {
                  setFieldValue("buttonLabel", "");
                  setFieldValue("buttonUrl", "");
                }
                // Con inscripción externa o sin botón, los cupos y asistentes
                // no los gestiona la app: el toggle queda bloqueado en false.
                setFieldValue("showAttendeesInfo", e.value === "INSCRIPTION");
              }}
              placeholder="Seleccionar modalidad"
            />
            <small className="text-color-secondary block mt-1">
              Define la acción principal del taller en la app.
            </small>
          </div>

          {values.buttonType === "CUSTOM_LINK" && (
            <>
              <div className="col-12 lg:col-4 field">
                <label htmlFor="buttonLabel">Etiqueta del botón *</label>
                <InputText
                  id="buttonLabel"
                  value={values.buttonLabel}
                  onChange={(e) => setFieldValue("buttonLabel", e.target.value)}
                  placeholder="Ej: Ver más info, Reservar lugar"
                  maxLength={40}
                  className={
                    touched.buttonLabel && errors.buttonLabel ? "p-invalid" : ""
                  }
                />
                {touched.buttonLabel && errors.buttonLabel && (
                  <small className="p-error">{errors.buttonLabel}</small>
                )}
              </div>

              <div className="col-12 lg:col-4 field">
                <label htmlFor="buttonUrl">URL destino *</label>
                <InputText
                  id="buttonUrl"
                  value={values.buttonUrl}
                  onChange={(e) => setFieldValue("buttonUrl", e.target.value)}
                  placeholder="https://..."
                  className={
                    touched.buttonUrl && errors.buttonUrl ? "p-invalid" : ""
                  }
                />
                {touched.buttonUrl && errors.buttonUrl && (
                  <small className="p-error">{errors.buttonUrl}</small>
                )}
              </div>
            </>
          )}

          <div className="col-12 field">
            <div className="flex align-items-center gap-3">
              <InputSwitch
                id="showAttendeesInfo"
                checked={values.showAttendeesInfo}
                disabled={values.buttonType !== "INSCRIPTION"}
                onChange={(e) => setFieldValue("showAttendeesInfo", e.value)}
              />
              <label htmlFor="showAttendeesInfo" className="mb-0">
                Mostrar datos de asistentes y cupos en la app
              </label>
            </div>
            <small className="text-color-secondary block mt-1">
              {values.buttonType === "INSCRIPTION"
                ? "Controla si la app muestra los cupos disponibles, el conteo de asistentes y la sección “Asistirán”."
                : "Solo disponible con “Inscripción estándar”: en las demás modalidades la inscripción no se gestiona en la app y estos datos permanecen ocultos."}
            </small>
          </div>
        </div>
      </FormSection>
      </FormSections>

      <div className="flex justify-content-end gap-3 mt-4">
        <Button
          label="Cancelar"
          icon="pi pi-times"
          outlined
          type="button"
          onClick={() => navigate(-1)}
        />
        <Button
          label={workshopData ? "Guardar Cambios" : "Crear Taller"}
          icon="pi pi-check"
          type="submit"
          disabled={isSubmitBlocked}
          loading={isLoading}
        />
      </div>
    </form>
  );
};
